import React, { useCallback, useEffect, useRef, useState } from 'react';

// Shared SVG icons — identical to VideoPlayer for design consistency
const PlayIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5.14v14.72a1 1 0 001.5.86l11.5-7.36a1 1 0 000-1.72L9.5 4.28A1 1 0 008 5.14z" />
  </svg>
);

const PauseIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
  </svg>
);

const VolumeHighIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 5L6 9H2v6h4l5 4V5z" />
    <path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.08" />
  </svg>
);

const VolumeMuteIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 5L6 9H2v6h4l5 4V5z" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

const VolumeLowIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 5L6 9H2v6h4l5 4V5z" />
    <path d="M15.54 8.46a5 5 0 010 7.08" />
  </svg>
);

const SPEED_OPTIONS = [1, 1.25, 1.5, 2, 0.5, 0.75];

// Number of equalizer bars
const BARS_FULLSCREEN = 24;
const BARS_COMPACT = 16;

const AudioPlayer = ({
  src,
  title,
  filename,
  fileSize,
  isDarkMode,
  // Props for audio state synchronization (panel ↔ fullscreen)
  audioRef: externalAudioRef,
  initialTimestamp = 0,
  autoPlay = false,
  onTimeUpdate
}) => {
  const internalAudioRef = useRef(null);
  const audioRef = externalAudioRef || internalAudioRef;
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const barsDataRef = useRef([]); // Current bar heights for smooth decay
  const containerRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [prevVolume, setPrevVolume] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(0);
  const [progressHover, setProgressHover] = useState(false);
  const [isCompact, setIsCompact] = useState(false);

  const progressBarRef = useRef(null);
  const isDragging = useRef(false);
  const hasSetInitialTimestamp = useRef(false);
  const hasTriggeredAutoPlay = useRef(false);

  // Animated feedback state
  const [feedbackIcon, setFeedbackIcon] = useState(null);
  const feedbackKey = useRef(0);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Detect compact mode (side panel)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsCompact(entry.contentRect.width < 500);
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // --- Audio visualization ---
  // Note: Web Audio API's createMediaElementSource() requires CORS headers on the audio source.
  // GCS signed URLs don't provide these, and connecting without CORS causes the browser to
  // MUTE the audio entirely as a security measure. So we use a wave animation instead,
  // which is synced to playback state and looks great without needing frequency data.

  // --- Canvas equalizer rendering ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const resizeCanvas = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    resizeCanvas();

    const primaryColor = isDarkMode ? '#7dc3c3' : '#2e7e7e';
    const barCount = isCompact ? BARS_COMPACT : BARS_FULLSCREEN;

    // Initialize bars data if needed — resting height of 10px for attractive idle state
    if (barsDataRef.current.length !== barCount) {
      barsDataRef.current = new Array(barCount).fill(0).map((_, i) => {
        // Gentle wave pattern for initial resting state
        return 6 + Math.sin(i * 0.5) * 4;
      });
    }

    const draw = () => {
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;
      ctx.clearRect(0, 0, width, height);

      const totalGap = (barCount - 1) * 2;
      const barWidth = Math.max(2, (width - totalGap) / barCount);

      if (playing) {
        // Animated wave visualization synced to playback
        const time = Date.now() / 1000;
        for (let i = 0; i < barCount; i++) {
          const phase = (i / barCount) * Math.PI * 3;
          const wave1 = Math.sin(time * 2.5 + phase) * 0.3;
          const wave2 = Math.sin(time * 1.7 + phase * 1.3) * 0.2;
          const wave3 = Math.sin(time * 3.1 + phase * 0.7) * 0.15;
          const combined = 0.45 + wave1 + wave2 + wave3;
          const targetHeight = Math.max(8, combined * height * 0.75);
          barsDataRef.current[i] += (targetHeight - barsDataRef.current[i]) * 0.15;
        }
      } else {
        // Decay bars to gentle resting wave when paused
        for (let i = 0; i < barCount; i++) {
          const restHeight = 6 + Math.sin(i * 0.5) * 4;
          barsDataRef.current[i] += (restHeight - barsDataRef.current[i]) * 0.05;
        }
      }

      // Draw bars
      for (let i = 0; i < barCount; i++) {
        const barHeight = barsDataRef.current[i];
        const x = i * (barWidth + 2);
        const y = height - barHeight;

        // Opacity based on height — taller bars more opaque
        const opacity = 0.4 + (barHeight / height) * 0.6;
        ctx.fillStyle = primaryColor;
        ctx.globalAlpha = Math.min(1, opacity);

        // Rounded top
        const radius = Math.min(barWidth / 2, 2);
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + barWidth - radius, y);
        ctx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + radius);
        ctx.lineTo(x + barWidth, height);
        ctx.lineTo(x, height);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      animFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    const resizeObserver = new ResizeObserver(resizeCanvas);
    resizeObserver.observe(canvas.parentElement);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      resizeObserver.disconnect();
    };
  }, [isDarkMode, playing, isCompact]);

  // --- Play/Pause ---
  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      audio.play().catch(() => setError(true));
    } else {
      audio.pause();
    }
  }, []);

  // --- Animated feedback ---
  const showFeedback = useCallback((type) => {
    feedbackKey.current += 1;
    setFeedbackIcon(type);
    setTimeout(() => setFeedbackIcon(null), 600);
  }, []);

  // --- Progress bar ---
  const getTimeFromPosition = useCallback((clientX) => {
    const bar = progressBarRef.current;
    if (!bar || !duration) return 0;
    const rect = bar.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return fraction * duration;
  }, [duration]);

  const handleProgressMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    const time = getTimeFromPosition(e.clientX);
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = time;
      setCurrentTime(time);
    }

    const handleDragMove = (moveEvent) => {
      const dragTime = getTimeFromPosition(moveEvent.clientX);
      if (audioRef.current) {
        audioRef.current.currentTime = dragTime;
        setCurrentTime(dragTime);
      }
    };

    const handleDragEnd = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.body.style.userSelect = '';
    };

    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
  }, [getTimeFromPosition]);

  const handleProgressHover = useCallback((e) => {
    const bar = progressBarRef.current;
    if (!bar || !duration) return;
    const rect = bar.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setHoverTime(fraction * duration);
    setHoverPosition(fraction * 100);
  }, [duration]);

  // --- Volume ---
  const handleVolumeChange = useCallback((value) => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = value;
      setVolume(value);
      if (value > 0) setPrevVolume(value);
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (volume > 0) {
      handleVolumeChange(0);
    } else {
      handleVolumeChange(prevVolume || 1);
    }
  }, [volume, prevVolume, handleVolumeChange]);

  // --- Speed ---
  const cycleSpeed = useCallback(() => {
    setPlaybackSpeed(prev => {
      const currentIdx = SPEED_OPTIONS.indexOf(prev);
      const nextIdx = (currentIdx + 1) % SPEED_OPTIONS.length;
      const newSpeed = SPEED_OPTIONS[nextIdx];
      if (audioRef.current) audioRef.current.playbackRate = newSpeed;
      return newSpeed;
    });
  }, []);

  // --- Seek ---
  const seekBy = useCallback((seconds) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.max(0, Math.min(duration, audio.currentTime + seconds));
    }
  }, [duration]);

  // Set initial timestamp when audio loads (for panel↔fullscreen handoff)
  useEffect(() => {
    const audio = audioRef.current;
    if (audio && initialTimestamp > 0 && !hasSetInitialTimestamp.current) {
      const handleCanPlay = () => {
        if (!hasSetInitialTimestamp.current) {
          audio.currentTime = initialTimestamp;
          hasSetInitialTimestamp.current = true;
        }
      };
      if (audio.readyState >= 3) {
        audio.currentTime = initialTimestamp;
        hasSetInitialTimestamp.current = true;
      } else {
        audio.addEventListener('canplay', handleCanPlay, { once: true });
        return () => audio.removeEventListener('canplay', handleCanPlay);
      }
    }
  }, [initialTimestamp, audioRef]);

  // Reset flags on src change
  useEffect(() => {
    hasSetInitialTimestamp.current = false;
    hasTriggeredAutoPlay.current = false;
    setPlaybackSpeed(1);
    if (audioRef.current) audioRef.current.playbackRate = 1;
  }, [src, audioRef]);

  // Auto-play on mount or when autoPlay prop is true (handoff from fullscreen)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !src) return;
    if (hasTriggeredAutoPlay.current) return;

    const shouldAutoPlay = autoPlay || initialTimestamp === 0;
    if (shouldAutoPlay) {
      const delay = initialTimestamp > 0 ? 200 : 100;
      const playTimer = setTimeout(() => {
        if (!hasTriggeredAutoPlay.current) {
          hasTriggeredAutoPlay.current = true;
          audio.play().catch(() => {});
        }
      }, delay);
      return () => clearTimeout(playTimer);
    }
  }, [src, initialTimestamp, autoPlay, audioRef]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => setPlaying(true);
    const handlePause = () => setPlaying(false);
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      // Notify parent of time updates for panel↔fullscreen sync
      if (onTimeUpdate) onTimeUpdate(audio.currentTime);
    };
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setLoading(false);
      setError(false);
    };
    const handleError = () => { setError(true); setLoading(false); };
    const handleEnded = () => { setPlaying(false); setCurrentTime(0); };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleError);
    audio.addEventListener('ended', handleEnded);
    audio.volume = volume;

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [volume]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tagName = e.target.tagName;
      const isEditable = e.target.isContentEditable;
      if (tagName === 'INPUT' || tagName === 'TEXTAREA' || isEditable) return;

      switch (e.key) {
        case ' ':
        case 'k':
        case 'K':
          e.preventDefault();
          togglePlayPause();
          showFeedback(audioRef.current?.paused ? 'pause' : 'play');
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          toggleMute();
          break;
        case 'j':
        case 'J':
          e.preventDefault();
          seekBy(-10);
          break;
        case 'l':
        case 'L':
          e.preventDefault();
          seekBy(10);
          break;
        case '>':
          e.preventDefault();
          cycleSpeed();
          break;
        case '<':
          e.preventDefault();
          setPlaybackSpeed(prev => {
            const currentIdx = SPEED_OPTIONS.indexOf(prev);
            const nextIdx = (currentIdx - 1 + SPEED_OPTIONS.length) % SPEED_OPTIONS.length;
            const newSpeed = SPEED_OPTIONS[nextIdx];
            if (audioRef.current) audioRef.current.playbackRate = newSpeed;
            return newSpeed;
          });
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [togglePlayPause, toggleMute, seekBy, cycleSpeed, showFeedback]);

  const themeClass = isDarkMode ? 'dark' : 'light';
  const VolumeIcon = volume === 0 ? VolumeMuteIcon : volume < 0.5 ? VolumeLowIcon : VolumeHighIcon;

  if (error) {
    return (
      <div className={`ap-error ${themeClass}`}>
        <p>Unable to play audio</p>
        <span className="ap-error-filename">{filename || title || 'Audio file'}</span>
        <button className="vp-error-retry" onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  const progressPercent = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div ref={containerRef} className={`audio-player-container ${themeClass}`}>
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* Animated feedback overlay */}
      {feedbackIcon && (
        <div className={`vp-feedback ${themeClass}`} key={feedbackKey.current}>
          <div className="vp-feedback-circle">
            {feedbackIcon === 'play' && <PlayIcon size={32} />}
            {feedbackIcon === 'pause' && <PauseIcon size={32} />}
          </div>
        </div>
      )}

      {/* Equalizer visualization */}
      <div className={`ap-visualizer ${themeClass}`}>
        <canvas ref={canvasRef} />
      </div>



      {/* Controls — reuse vp- classes for shared styling */}
      <div className="ap-controls">
        {/* Progress bar */}
        <div
          className={`vp-progress ${progressHover ? 'hover' : ''}`}
          ref={progressBarRef}
          onMouseDown={handleProgressMouseDown}
          onMouseMove={handleProgressHover}
          onMouseEnter={() => setProgressHover(true)}
          onMouseLeave={() => { setProgressHover(false); setHoverTime(null); }}
        >
          <div className="vp-progress-track">
            <div className="vp-progress-filled" style={{ width: `${progressPercent}%` }} />
            <div className="vp-progress-thumb" style={{ left: `${progressPercent}%` }} />
          </div>
          {hoverTime !== null && (
            <div className="vp-progress-tooltip" style={{ left: `${hoverPosition}%` }}>
              {formatTime(hoverTime)}
            </div>
          )}
        </div>

        {/* Control row */}
        <div className="vp-control-row">
          <div className="vp-control-left">
            <button
              className="vp-btn vp-btn-play"
              onClick={(e) => { e.stopPropagation(); togglePlayPause(); showFeedback(audioRef.current?.paused ? 'pause' : 'play'); }}
            >
              {playing ? <PauseIcon size={18} /> : <PlayIcon size={18} />}
            </button>

            <div className="vp-volume">
              <button className="vp-btn vp-btn-volume" onClick={(e) => { e.stopPropagation(); toggleMute(); }}>
                <VolumeIcon />
              </button>
              <input
                type="range"
                className="vp-volume-slider"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
              />
            </div>

            <span className="vp-time">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <div className="vp-control-right">
            <button
              className={`vp-btn vp-btn-speed ${playbackSpeed !== 1 ? 'active' : ''}`}
              onClick={(e) => { e.stopPropagation(); cycleSpeed(); }}
              title="Playback speed"
            >
              {playbackSpeed}×
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
